/**
 * Onglet Analytics BI — Répartition & catégorisation des dépenses bancaires (Fév 2026).
 * Réutilise l'endpoint GET /api/bank/analytics/expenses
 *
 * Vue CFO/DAF inspirée des meilleures pratiques BI Finance :
 * - KPIs synthèse (total dépenses, variation M/M-1, ticket moyen, non catégorisé)
 * - Donut chart : répartition par catégorie
 * - Bar chart horizontal : top 10 contreparties
 * - Line chart : évolution 12 mois glissants
 * - Alertes anomalies (>2σ historique)
 * - Table détaillée par catégorie
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, PieChart as PieIcon, BarChart3, AlertTriangle,
  Users, Calendar, RefreshCw, Loader2, ArrowUpRight, ArrowDownRight,
  CheckCircle2 as CheckCircle2Icon,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, Legend,
} from 'recharts';
import PeriodEvolutionCard from './PeriodEvolutionCard';
import PeriodClosureSelector from './PeriodClosureSelector';
import SavedViewsMenu from '../common/SavedViewsMenu';
import BankAlertsConfigModal from './BankAlertsConfigModal';
import { Bell } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
const MONTHS_FR = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Août','Sep','Oct','Nov','Déc'];
const shortMonth = (p) => {
  if (!p) return '';
  const [y, m] = p.split('-');
  return `${MONTHS_FR[+m - 1]} ${y.slice(2)}`;
};

// Palette BI-friendly (11 couleurs distinctes, contrastes suffisants)
const CATEGORY_COLORS = [
  '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
  '#06b6d4', '#f43f5e', '#84cc16', '#a855f7', '#f97316', '#94a3b8',
];

export default function BankAnalyticsTab({ accountId, period, onChangePeriod }) {
  const [data, setData] = useState(null);
  const [fullDashboard, setFullDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [periodHistory, setPeriodHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [alertsModalOpen, setAlertsModalOpen] = useState(false);
  const [alertHistory, setAlertHistory] = useState([]);
  // Période active (peut être modifiée par le sélecteur ou en cliquant sur le chart)
  const [activePeriod, setActivePeriod] = useState(period);

  useEffect(() => { setActivePeriod(period); }, [period]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: res }, fullRes] = await Promise.all([
        axios.get(`${API}/bank/analytics/expenses`, { params: { account_id: accountId || undefined, period: activePeriod } }),
        accountId ? axios.get(`${API}/bank/analytics/full-dashboard/${accountId}`, { params: { period: activePeriod } }) : Promise.resolve({ data: null }),
      ]);
      setData(res);
      setFullDashboard(fullRes.data);
    } catch (e) {
      setData(null);
    } finally { setLoading(false); }
  }, [accountId, activePeriod]);

  const fetchHistory = useCallback(async () => {
    if (!accountId) return;
    setHistoryLoading(true);
    try {
      const [{ data }, alertsRes] = await Promise.all([
        axios.get(`${API}/bank/analytics/period-history`, {
          params: { account_id: accountId, months: 12 },
        }),
        axios.get(`${API}/bank/alerts/history`, {
          params: { account_id: accountId, limit: 5 },
        }).catch(() => ({ data: { items: [] } })),
      ]);
      setPeriodHistory(data?.periods || []);
      setAlertHistory(alertsRes.data?.items || []);
    } catch (e) {
      setPeriodHistory([]);
      setAlertHistory([]);
    } finally { setHistoryLoading(false); }
  }, [accountId]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleLoadSavedView = (filters) => {
    if (filters?.period) {
      setActivePeriod(filters.period);
      onChangePeriod?.(filters.period);
    }
  };

  if (loading && !data) {
    return <div className="p-8 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline mr-2" />Chargement analytics...</div>;
  }
  if (!data) {
    return <div className="p-8 text-center text-slate-400">Aucune donnée pour cette période.</div>;
  }

  const k = data.kpis || {};
  const variationPositive = (k.variation_pct || 0) > 0;

  return (
    <div className="space-y-4" data-testid="bank-analytics-tab">
      {/* HEADER + Sélecteur période + Refresh + Vues */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-slate-900">Analytics dépenses</h3>
          <span className="text-xs text-slate-500">· Période {shortMonth(data.period)}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodClosureSelector
            history={periodHistory}
            value={activePeriod}
            loading={historyLoading}
            onChange={(p) => {
              const newP = p || new Date().toISOString().slice(0, 7);
              setActivePeriod(newP);
              onChangePeriod?.(newP);
            }}
          />
          <SavedViewsMenu
            scope="bank"
            currentFilters={{ period: activePeriod, account_id: accountId }}
            onLoad={handleLoadSavedView}
          />
          {accountId && (
            <button
              onClick={() => setAlertsModalOpen(true)}
              className="text-xs inline-flex items-center gap-1 border border-slate-200 bg-white rounded-lg px-2.5 py-1 hover:bg-blue-50"
              data-testid="bank-alerts-open-btn"
              title="Configurer les alertes CFO">
              <Bell className="h-3.5 w-3.5 text-blue-600" /> Alertes
              {alertHistory.length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-4 h-4 text-[10px] font-semibold bg-rose-100 text-rose-700 rounded-full px-1">
                  {alertHistory.length}
                </span>
              )}
            </button>
          )}
          <button onClick={() => { fetchAnalytics(); fetchHistory(); }} disabled={loading}
            className="text-xs inline-flex items-center gap-1 border border-slate-200 rounded-lg px-2.5 py-1 hover:bg-slate-50 disabled:opacity-50"
            data-testid="analytics-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </button>
        </div>
      </div>

      {/* Évolution des soldes de clôture (12 mois) */}
      {accountId && (
        <PeriodEvolutionCard
          title="Évolution du solde par clôture mensuelle"
          subtitle="Solde de clôture (ligne) · Crédits & débits mensuels (barres)"
          data={periodHistory}
          loading={historyLoading}
          selectedPeriod={activePeriod}
          onSelectPeriod={(p) => { setActivePeriod(p); onChangePeriod?.(p); }}
        />
      )}

      {/* KPIs synthèse */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-rose-50 to-white p-4" data-testid="analytics-kpi-total">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-rose-700 font-semibold">
            <ArrowDownRight className="h-3.5 w-3.5" /> Dépenses du mois
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{fmt(k.total_expenses)} <span className="text-xs font-normal text-slate-500">XOF</span></div>
          <div className="text-xs text-slate-500 mt-1">{k.count_tx || 0} transactions</div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="analytics-kpi-variation">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 font-medium">
            {variationPositive ? <TrendingUp className="h-3.5 w-3.5 text-rose-600" /> : <TrendingDown className="h-3.5 w-3.5 text-emerald-600" />}
            Variation M-1
          </div>
          <div className={`text-2xl font-bold mt-1 ${k.variation_pct == null ? 'text-slate-400' : variationPositive ? 'text-rose-600' : 'text-emerald-600'}`}>
            {k.variation_pct == null ? '—' : `${variationPositive ? '+' : ''}${k.variation_pct}%`}
          </div>
          <div className="text-xs text-slate-500 mt-1">vs {shortMonth(data.period_prev)} : {fmt(k.total_expenses_prev)} XOF</div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="analytics-kpi-avg">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 font-medium">
            <Calendar className="h-3.5 w-3.5" /> Dépense moyenne
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{fmt(k.avg_ticket)} <span className="text-xs font-normal text-slate-500">XOF</span></div>
          <div className="text-xs text-slate-500 mt-1">par transaction</div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-amber-50 to-white p-4" data-testid="analytics-kpi-uncategorized">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-amber-700 font-semibold">
            <AlertTriangle className="h-3.5 w-3.5" /> À catégoriser
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{k.uncategorized_pct || 0}%</div>
          <div className="text-xs text-slate-500 mt-1">{fmt(k.uncategorized_amount)} XOF à qualifier</div>
        </div>
      </div>

      {/* Anomalies (si présentes) */}
      {data.anomalies?.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="analytics-anomalies">
          <div className="flex items-center gap-2 text-sm font-semibold text-rose-800 mb-2">
            <AlertTriangle className="h-4 w-4" /> Anomalies détectées ({data.anomalies.length})
          </div>
          <div className="space-y-2">
            {data.anomalies.map((a, i) => (
              <div key={i} className="text-xs bg-white border border-rose-200 rounded-lg p-2.5 flex justify-between items-center">
                <div>
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase mr-2 ${a.severity === 'high' ? 'bg-rose-600 text-white' : 'bg-amber-500 text-white'}`}>
                    {a.severity}
                  </span>
                  <span className="font-medium text-slate-800">{a.category}</span>
                  <span className="text-slate-500 ml-1">— {a.delta_pct}% vs moyenne 6 mois</span>
                </div>
                <div className="text-slate-700 font-mono">{fmt(a.current)} XOF <span className="text-slate-400">(moy: {fmt(a.historical_avg)})</span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Business buckets + Qualité + Encaissements (Fév 2026 — spec CFO) */}
      {fullDashboard && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="business-buckets">
            {[
              { key: 'operational', label: 'Dépenses opérationnelles', color: 'blue', hint: 'Chauffeur, carburant, transporteur' },
              { key: 'admin', label: 'Dépenses administratives', color: 'violet', hint: 'Salaires, loyer, services' },
              { key: 'financial', label: 'Frais financiers', color: 'amber', hint: 'Bank + Wave si consolidé' },
              { key: 'other', label: 'Autres / À catégoriser', color: 'slate', hint: 'À qualifier' },
            ].map(b => {
              const bucket = fullDashboard.expenses.buckets[b.key];
              const total = fullDashboard.main.sorties || 1;
              const pct = total ? Math.round((bucket.total / total) * 100) : 0;
              return (
                <div key={b.key} className={`rounded-xl border p-3 bg-gradient-to-b from-${b.color}-50 to-white border-${b.color}-200`} data-testid={`bucket-${b.key}`}>
                  <div className={`text-[10px] uppercase tracking-wide font-semibold text-${b.color}-700`}>{b.label}</div>
                  <div className="text-lg font-bold text-slate-900 mt-1">{fmt(bucket.total)} <span className="text-[10px] font-normal text-slate-500">XOF · {pct}%</span></div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{bucket.count} tx · {b.hint}</div>
                </div>
              );
            })}
          </div>

          {/* Encaissements + Qualité comptable */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4" data-testid="section-inflows">
              <div className="flex items-center gap-2 mb-3">
                <ArrowUpRight className="h-4 w-4 text-emerald-600" />
                <h4 className="text-sm font-semibold text-slate-800">Encaissements du mois</h4>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase text-slate-500 font-medium">Total encaissé</div>
                  <div className="text-lg font-bold text-slate-900">{fmt(fullDashboard.inflows.total)} <span className="text-[10px] font-normal">XOF</span></div>
                  <div className="text-[10px] text-slate-500">{fullDashboard.inflows.count} lignes · moy {fmt(fullDashboard.inflows.avg)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500 font-medium">Rapprochés</div>
                  <div className="text-lg font-bold text-emerald-700">{fmt(fullDashboard.inflows.matched_amount)}</div>
                  <div className="text-[10px] text-slate-500">{fullDashboard.inflows.matched_count} lignes</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500 font-medium">Non rapprochés</div>
                  <div className="text-lg font-bold text-amber-700">{fmt(fullDashboard.inflows.unmatched_amount)}</div>
                  <div className="text-[10px] text-slate-500">{fullDashboard.inflows.unmatched_count} lignes à identifier</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500 font-medium">Virements reçus / Chèques</div>
                  <div className="text-sm font-semibold text-slate-800">{fmt(fullDashboard.inflows.virements_recus)} / {fmt(fullDashboard.inflows.cheques_encaisses)}</div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4" data-testid="section-quality">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2Icon className="h-4 w-4 text-blue-600" />
                <h4 className="text-sm font-semibold text-slate-800">Qualité comptable</h4>
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <div className={`text-4xl font-bold ${fullDashboard.quality.score >= 80 ? 'text-emerald-600' : fullDashboard.quality.score >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>
                    {fullDashboard.quality.score}
                    <span className="text-lg text-slate-400">/100</span>
                  </div>
                  <div className="text-[10px] text-slate-500">{fullDashboard.quality.deductions.length} anomalie{fullDashboard.quality.deductions.length > 1 ? 's' : ''}</div>
                </div>
                <div className="flex-1 space-y-1 text-xs">
                  {fullDashboard.quality.deductions.length === 0 ? (
                    <div className="text-emerald-600 flex items-center gap-1"><CheckCircle2Icon className="h-3 w-3" /> Aucune déduction</div>
                  ) : fullDashboard.quality.deductions.slice(0, 4).map((d, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-slate-600 truncate">{d.reason}</span>
                      <span className="text-rose-600 font-semibold flex-shrink-0 ml-2">{d.points} pts</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Charts row 1: Donut catégories + Line trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Donut catégories */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <PieIcon className="h-4 w-4 text-blue-600" />
            <h4 className="text-sm font-semibold text-slate-800">Répartition par catégorie</h4>
          </div>
          {data.categories?.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">Aucune dépense sur la période</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={data.categories} dataKey="total" nameKey="label" cx="50%" cy="50%"
                       innerRadius={45} outerRadius={85} paddingAngle={2}>
                    {data.categories.map((_, i) => (
                      <Cell key={i} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                    ))}
                  </Pie>
                  <ReTooltip formatter={(v, n, p) => [`${fmt(v)} XOF (${p.payload.pct}%)`, p.payload.label]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 text-xs max-h-52 overflow-y-auto pr-2">
                {data.categories.map((c, i) => (
                  <div key={c.key} className="flex items-center justify-between gap-2" data-testid={`analytics-cat-${c.key}`}>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }} />
                      <span className="truncate text-slate-700">{c.label}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-slate-500 font-mono">{fmt(c.total)}</span>
                      <span className="text-slate-400 w-10 text-right">{c.pct}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Line trend 12 mois */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-violet-600" />
            <h4 className="text-sm font-semibold text-slate-800">Évolution mensuelle (12 mois glissants)</h4>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.trend_12m.map(t => ({ ...t, label: shortMonth(t.period) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} />
              <ReTooltip formatter={(v) => `${fmt(v)} XOF`} />
              <Line type="monotone" dataKey="total" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3, fill: '#8b5cf6' }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts row 2: Top counterparties (bar horizontal) + Day of week */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-emerald-600" />
            <h4 className="text-sm font-semibold text-slate-800">Top contreparties</h4>
          </div>
          {data.top_counterparties?.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">Aucune contrepartie identifiée</div>
          ) : (
            <div className="space-y-2">
              {data.top_counterparties.slice(0, 8).map((c, i) => {
                const maxTotal = data.top_counterparties[0].total || 1;
                const widthPct = (c.total / maxTotal) * 100;
                return (
                  <div key={c.name} className="text-xs" data-testid={`analytics-cp-${i}`}>
                    <div className="flex justify-between mb-0.5">
                      <span className="truncate flex-1 text-slate-700 mr-2">{c.name}</span>
                      <span className="text-slate-600 font-mono flex-shrink-0">{fmt(c.total)} XOF · {c.count} tx</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full transition-all"
                           style={{ width: `${widthPct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-4 w-4 text-orange-600" />
            <h4 className="text-sm font-semibold text-slate-800">Dépenses par jour de semaine</h4>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.by_day_of_week}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} />
              <ReTooltip formatter={(v) => `${fmt(v)} XOF`} />
              <Bar dataKey="total" fill="#f97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Historique alertes envoyées */}
      {accountId && alertHistory.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="bank-alerts-history">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="h-4 w-4 text-rose-600" />
            <h4 className="text-sm font-semibold text-slate-800">Historique des alertes envoyées ({alertHistory.length})</h4>
          </div>
          <div className="space-y-2">
            {alertHistory.map(a => (
              <div key={a.id} className="flex items-center justify-between text-xs border-l-2 border-rose-400 bg-rose-50/50 px-3 py-2 rounded">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-800 truncate">{a.subject}</div>
                  <div className="text-slate-500">
                    {a.alert_type === 'variation' ? `Variation : ${a.actual_value}% (seuil ${a.threshold_value}%)` : `Solde ${fmt(a.actual_value)} < plancher ${fmt(a.threshold_value)}`}
                    · {a.channels?.join(' + ')}
                  </div>
                </div>
                <div className="text-[10px] text-slate-400 flex-shrink-0 ml-2">
                  {a.triggered_at ? new Date(a.triggered_at).toLocaleDateString('fr-FR') : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal alertes */}
      {accountId && (
        <BankAlertsConfigModal
          open={alertsModalOpen}
          onClose={() => { setAlertsModalOpen(false); fetchHistory(); }}
          accountId={accountId}
          accountLabel={data.account_label || `Compte ${accountId.slice(0, 8)}`}
        />
      )}
    </div>
  );
}
